import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { MapPin, ArrowUpRight, Sprout } from "lucide-react";

const WINE_BOTTLES = "https://images.pexels.com/photos/5004027/pexels-photo-5004027.jpeg";
const CHEESE = "https://images.pexels.com/photos/13996604/pexels-photo-13996604.jpeg";
const OLIVE_OIL = "https://static.prod-images.emergentagent.com/jobs/29081a3e-ac58-482c-aff5-9e6ace12b60e/images/b30e383a659f5ab2fd5d39196fa9a13b882ff9621acadcdd84293883937313f7.png";
const PRODUCER_PORTRAIT = "https://static.prod-images.emergentagent.com/jobs/29081a3e-ac58-482c-aff5-9e6ace12b60e/images/78f8b1c5a7dc4063e49935198bcf10f414cd58f3b196edea1ad97750c90d96ed.png";

// Map por região para imagem mais coerente com o produtor
function imageForProducer(p, idx) {
    const region = (p.region || "").toLowerCase();
    if (region.includes("alentejo")) return CHEESE;
    if (region.includes("beira alta")) return OLIVE_OIL;
    if (region.includes("dão")) return PRODUCER_PORTRAIT;
    if (region.includes("vinho verde")) return WINE_BOTTLES;
    if (region.includes("douro")) return WINE_BOTTLES;
    const pool = [WINE_BOTTLES, CHEESE, PRODUCER_PORTRAIT, OLIVE_OIL];
    return pool[idx % pool.length];
}

const HERO_BG = WINE_BOTTLES;

export default function Producers() {
    const [producers, setProducers] = useState([]);
    const [activeRegion, setActiveRegion] = useState("Todas");

    useEffect(() => {
        api.get("/producers").then((r) => setProducers(r.data));
    }, []);

    const regions = useMemo(() => {
        const set = new Set(producers.map((p) => p.region));
        return ["Todas", ...Array.from(set)];
    }, [producers]);

    const filtered = useMemo(
        () => (activeRegion === "Todas" ? producers : producers.filter((p) => p.region === activeRegion)),
        [producers, activeRegion]
    );

    const totalProducts = producers.reduce(
        (sum, p) => sum + (p.products?.length || 0),
        0
    );

    return (
        <>
            <Navbar />

            {/* HERO */}
            <section className="relative h-[72vh] min-h-[560px] bg-wine-deep overflow-hidden">
                <img
                    src={HERO_BG}
                    alt="Vinhas portuguesas"
                    className="absolute inset-0 w-full h-full object-cover kenburns opacity-95"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-wine-deep/55 via-wine-deep/55 to-wine-deep/95" />
                <div className="relative container-edge h-full flex flex-col justify-end pb-20 text-cream">
                    <span className="label-overline text-gold-soft" data-testid="producers-overline">
                        Os nossos produtores
                    </span>
                    <h1 className="mt-6 font-heading text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight max-w-4xl text-shadow-soft">
                        Histórias com nome,{" "}
                        <em className="text-gold-soft not-italic">morada</em> e mãos.
                    </h1>
                    <p className="mt-8 max-w-2xl text-cream/80 text-base md:text-lg leading-relaxed">
                        Pequenos produtores portugueses que conhecemos pessoalmente — adega a adega,
                        olival a olival, cave a cave. Cada garrafa, cada frasco, cada queijo carrega
                        a memória de quem o fez.
                    </p>
                </div>
            </section>

            {/* STATS STRIP */}
            <section className="bg-cream border-b border-gold-soft/30">
                <div className="container-edge py-10 grid grid-cols-3 gap-6 text-center md:text-left">
                    <Stat label="Regiões" value={regions.length - 1} />
                    <Stat label="Produtores" value={producers.length} />
                    <Stat label="Referências" value={totalProducts || "+30"} />
                </div>
            </section>

            {/* REGION FILTER */}
            <section className="bg-cream sticky top-24 z-30 border-b border-wine-dark/5">
                <div className="container-edge py-6 flex flex-wrap items-center gap-3" data-testid="region-filters">
                    <span className="label-overline text-wine-dark/60 mr-2 hidden md:inline">
                        Filtrar por região
                    </span>
                    {regions.map((r) => (
                        <button
                            key={r}
                            onClick={() => setActiveRegion(r)}
                            className={`label-overline px-5 py-2.5 rounded-full border transition-all duration-300 ${
                                activeRegion === r
                                    ? "bg-wine text-cream border-wine shadow-sm"
                                    : "bg-transparent border-wine-dark/20 text-wine-dark hover:border-wine hover:text-wine"
                            }`}
                            data-testid={`region-${r}`}
                        >
                            {r}
                        </button>
                    ))}
                </div>
            </section>

            {/* GRELHA DE PRODUTORES */}
            <section className="bg-cream py-14 md:py-20">
                <div className="container-edge">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {filtered.map((p, idx) => {
                            const img = imageForProducer(p, idx);
                            const count = p.products?.length || 0;
                            return (
                                <Link
                                    key={p.id}
                                    to={`/produtores/${p.id}`}
                                    data-testid={`producer-card-${p.id}`}
                                    className="group flex flex-col bg-white border border-gold-soft/25 hover:border-gold-soft/70 hover:shadow-xl rounded-lg overflow-hidden transition-all duration-500"
                                >
                                    <div className="relative aspect-[4/3] overflow-hidden bg-cream-warm">
                                        <img
                                            src={img}
                                            alt={p.producer_name}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <span className="absolute top-3 left-3 bg-cream/90 backdrop-blur-sm text-wine label-overline text-[9px] px-2.5 py-1 rounded-sm flex items-center gap-1 shadow-sm">
                                            <MapPin className="h-3 w-3" strokeWidth={2} /> {p.region}
                                        </span>
                                    </div>
                                    <div className="p-5 flex flex-col gap-2 flex-1">
                                        <h2 className="font-heading text-xl text-wine-dark leading-snug line-clamp-1">
                                            {p.producer_name}
                                        </h2>
                                        <p className="text-sm text-wine-dark/60 leading-relaxed line-clamp-3 min-h-[3.9rem]">
                                            {p.story}
                                        </p>
                                        <div className="mt-auto pt-4 flex items-center justify-between border-t border-gold-soft/15">
                                            <span className="label-overline text-[10px] text-wine-dark/50">
                                                {count > 0 ? `${count} ${count > 1 ? "referências" : "referência"}` : "Ver catálogo"}
                                            </span>
                                            <span
                                                className="inline-flex items-center gap-1.5 label-overline text-[10px] text-wine group-hover:text-gold-deep transition-colors"
                                                data-testid={`producer-cta-${p.id}`}
                                            >
                                                Ver perfil
                                                <ArrowUpRight
                                                    className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                                    strokeWidth={1.5}
                                                />
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>

                    {filtered.length === 0 && (
                        <div className="py-20 text-center text-wine-dark/60 label-overline" data-testid="producers-empty">
                            Sem produtores nesta região.
                        </div>
                    )}
                </div>
            </section>

            {/* VENDOR CTA */}
            <section className="bg-wine-dark text-cream py-20 md:py-28 relative overflow-hidden">
                <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <Sprout className="h-8 w-8 text-gold-soft" strokeWidth={1.5} />
                        <h3 className="mt-6 font-heading text-4xl md:text-5xl leading-[1.05]">
                            É produtor artesanal português?
                        </h3>
                        <p className="mt-6 text-cream/75 text-lg leading-relaxed max-w-xl">
                            Junte-se a uma curadoria editorial premium. Mostre o seu trabalho a
                            milhares de apreciadores e receba pagamentos automáticos.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex lg:justify-end">
                        <Link to="/register?role=vendor" className="btn-gold" data-testid="vendor-cta">
                            Candidatar a minha quinta
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </>
    );
}

function Stat({ label, value }) {
    return (
        <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
            <span className="font-heading text-4xl md:text-5xl text-wine-dark">{value}</span>
            <span className="label-overline text-wine-dark/60">{label}</span>
        </div>
    );
}
